// 6. 発表会 基本設定（管理者向け）。発表会の全設定の置き場。
// レイアウトは既存ページ踏襲：ResizableEditArea(編集パネル)＋Breadcrumbs＋最下部 Reset/Submit。
// 申込ページ(ivy_website /recital/2026/entry?preview=1)を全画面プレビュー iframe として背面に表示し、
// 入力を postMessage で送って公演情報/料金/規約/メインビジュアル/締切を即反映する。
// §10.7：手動の受付トグルは廃止し、参加申込/パンフレット写真/音源照明の3チャネルを締切日時で自動判定。
import { useCallback, useEffect, useRef, useState } from "react";
import Breadcrumbs from "../../../components/Breadcrumbs";
import ResizableEditArea from "../../../components/common/ResizableEditArea";
import StatusMessage from "../../../components/common/StatusMessage";
import {
  getRecitalEvent,
  updateRecitalEvent,
  uploadImage,
  getInstructorTokens,
} from "../../../utils/recitalApi";
import "./recital.scss";

const RC_BREADCRUMBS = [
  { title: "Home", path: "/" },
  { title: "Recital", path: "/recital/2026/setting" },
  { title: "Settings", path: "/recital/2026/setting" },
];

const ORIGIN = process.env.REACT_APP_ORIGIN; // 申込ページ(ivy_website) のオリジン

const EMPTY = {
  title: "",
  eventDate: "",
  eventWeekdayLabel: "",
  venue: "",
  address: "",
  feeGeneral: "",
  feeSibling: "",
  feeYoung: "",
  labelGeneral: "一般",
  labelSibling: "兄弟",
  labelYoung: "幼少",
  terms: "",
  entryDeadline: "",
  photoDeadline: "",
  submissionDeadline: "",
  transferDeadline: "",
  transferInfo: "",
  transferNote: "",
  photoNotes: "",
  openTime: "",
  partOpenTimes: [""],
  defaultTransitionSeconds: "",
  numParts: 1,
  mainVisualId: null,
  mainVisualUrl: "",
};

// API(event snake_case) → フォーム
const fromApi = (ev) => ({
  title: ev.title || "",
  eventDate: ev.event_date || "",
  eventWeekdayLabel: ev.event_weekday_label || "",
  venue: ev.venue || "",
  address: ev.address || "",
  feeGeneral: ev.fee_general ?? "",
  feeSibling: ev.fee_sibling ?? "",
  feeYoung: ev.fee_young ?? "",
  labelGeneral: ev.label_general || "一般",
  labelSibling: ev.label_sibling || "兄弟",
  labelYoung: ev.label_young || "幼少",
  terms: ev.terms || "",
  entryDeadline: ev.entry_deadline || "",
  photoDeadline: ev.photo_deadline || "",
  submissionDeadline: ev.submission_deadline || "",
  transferDeadline: ev.transfer_deadline || "",
  transferInfo: ev.transfer_info || "",
  transferNote: ev.transfer_note || "",
  photoNotes: ev.photo_notes || "",
  openTime: ev.open_time || "",
  partOpenTimes: ev.part_open_times
    ? ev.part_open_times.split(",")
    : ev.open_time
      ? [ev.open_time]
      : [""],
  defaultTransitionSeconds: ev.default_transition_seconds ?? "",
  numParts: ev.num_parts ?? 1,
  mainVisualId: ev.main_visual ? ev.main_visual.id : null,
  mainVisualUrl: ev.main_visual ? ev.main_visual.image_url : "",
});

export default function RecitalSettingPage() {
  const [form, setForm] = useState(EMPTY);
  const [loaded, setLoaded] = useState(null); // 直近サーバ値（Reset 用）
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("success");
  const iframeRef = useRef(null);

  // 講師ログインURLモーダル（§10.5🟦12・再発行なし）
  const [tokenModal, setTokenModal] = useState(false);
  const [instructors, setInstructors] = useState(null); // null=未読込
  const [copiedId, setCopiedId] = useState(null);
  const instructorUrl = (token) =>
    `${ORIGIN}/recital/2026/instructor?token=${token}`;

  // 受付中判定：申込締切が未来なら受付中（§10.7 日時自動判定）
  const open =
    !!form.entryDeadline && new Date(form.entryDeadline) > new Date();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  // 部別開演時刻：i 番目（0始まり＝部-1）を更新。0番目は openTime とも同期
  const setPartOpen = (i, v) =>
    setForm((f) => {
      const arr = [...(f.partOpenTimes || [])];
      while (arr.length <= i) arr.push("");
      arr[i] = v;
      return { ...f, partOpenTimes: arr, openTime: i === 0 ? v : f.openTime };
    });

  const notify = (text, type = "success") => {
    setMsgType(type);
    setMessage(text);
    setTimeout(() => setMessage(""), 3000);
  };

  // 初期ロード
  useEffect(() => {
    let alive = true;
    getRecitalEvent()
      .then((d) => {
        if (!alive) return;
        const f = fromApi(d.event);
        setForm(f);
        setLoaded(f);
      })
      .catch((e) => notify(e.message || "読み込みに失敗しました", "error"));
    return () => {
      alive = false;
    };
  }, []);

  // 申込ページ iframe へ現在の設定を送信
  const post = useCallback(() => {
    const win = iframeRef.current?.contentWindow;
    if (win && ORIGIN) {
      const align = window.innerWidth > 1024 ? "right" : "center";
      win.postMessage(
        {
          type: "RECITAL_PREVIEW",
          event: { ...form, mainVisual: form.mainVisualUrl },
          align,
        },
        ORIGIN,
      );
    }
  }, [form]);
  useEffect(() => {
    post();
  }, [post]);
  useEffect(() => {
    const onResize = () => post();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [post]);

  const focusPreview = useCallback((target) => {
    const win = iframeRef.current?.contentWindow;
    if (win && ORIGIN) {
      win.postMessage({ type: "RECITAL_PREVIEW_SCROLL", target }, ORIGIN);
    }
  }, []);

  // メインビジュアル：S3 アップロード（既存 image_upload 流用）→ id を保持
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await uploadImage(file);
      const img = res.uploaded_images?.[0] || res.images?.[0] || res?.[0];
      if (img) {
        setForm((f) => ({
          ...f,
          mainVisualId: img.id,
          mainVisualUrl: img.image_url,
        }));
      }
    } catch (err) {
      notify(err.message || "画像アップロードに失敗しました", "error");
    }
  };

  // 講師ログインURL一覧を開く（初回のみ取得）
  const openTokenModal = async () => {
    setTokenModal(true);
    if (instructors !== null) return;
    try {
      const d = await getInstructorTokens();
      setInstructors(d.instructors || []);
    } catch (err) {
      notify(err.message || "講師URLの取得に失敗しました", "error");
      setInstructors([]);
    }
  };

  const copyUrl = async (row) => {
    try {
      await navigator.clipboard.writeText(instructorUrl(row.token));
      setCopiedId(row.instructor_id);
      setTimeout(() => setCopiedId(null), 1500);
    } catch {
      notify("コピーに失敗しました", "error");
    }
  };

  const handleReset = () => loaded && setForm(loaded);
  const handleSubmit = async () => {
    try {
      const payload = {
        title: form.title,
        venue: form.venue,
        address: form.address,
        event_date: form.eventDate || "",
        event_weekday_label: form.eventWeekdayLabel || "",
        terms: form.terms,
        fee_general: form.feeGeneral,
        fee_sibling: form.feeSibling,
        fee_young: form.feeYoung,
        label_general: form.labelGeneral,
        label_sibling: form.labelSibling,
        label_young: form.labelYoung,
        entry_deadline: form.entryDeadline || "",
        photo_deadline: form.photoDeadline || "",
        submission_deadline: form.submissionDeadline || "",
        transfer_deadline: form.transferDeadline || "",
        transfer_info: form.transferInfo || "",
        transfer_note: form.transferNote || "",
        photo_notes: form.photoNotes || "",
        open_time: (form.partOpenTimes && form.partOpenTimes[0]) || form.openTime || "",
        part_open_times: (form.partOpenTimes || [])
          .slice(0, Math.max(1, Number(form.numParts) || 1))
          .join(","),
        default_transition_seconds: form.defaultTransitionSeconds,
        num_parts: form.numParts || 1,
        main_visual_id: form.mainVisualId,
      };
      const d = await updateRecitalEvent(payload);
      const f = fromApi(d.event);
      setForm(f);
      setLoaded(f);
      notify("保存しました");
    } catch (err) {
      notify(err.message || "保存に失敗しました", "error");
    }
  };

  return (
    <>
      <ResizableEditArea>
        {({ onResizeStart }) => (
          <>
            <Breadcrumbs
              breadcrumbs={RC_BREADCRUMBS}
              onResizeStart={onResizeStart}
            />
            <StatusMessage message={message} type={msgType} />

            <div className="rc" data-verify-recital-setting>
              <div className="rc-statusrow">
                <span
                  className={`rc-tag ${open ? "open" : "closed"}`}
                  data-verify-accepting={open ? "open" : "closed"}
                >
                  {open ? "受付中" : "締切"}
                </span>
              </div>

              <div className="rc-section">公演情報</div>
              <div className="rc-group">
                <div className="rc-hero">
                  <label className="rc-img-ph">
                    {form.mainVisualUrl ? (
                      // eslint-disable-next-line jsx-a11y/alt-text
                      <img src={form.mainVisualUrl} alt="メインビジュアル" />
                    ) : (
                      <svg viewBox="0 0 48 48" width="56" height="56" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <rect x="6" y="9" width="36" height="30" rx="3" />
                        <circle cx="17" cy="20" r="3.5" />
                        <path d="M10 35l9-9 7 7 5-5 7 7" />
                      </svg>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={onFile}
                      hidden
                    />
                  </label>

                  <div className="rc-hero-fields">
                    <div className="rc-field">
                      <label className="rc-label">公演名</label>
                      <input
                        type="text"
                        value={form.title}
                        onChange={(e) => set("title", e.target.value)}
                        onFocus={() => focusPreview(".rct-ev .title")}
                      />
                    </div>
                    <div className="rc-field">
                      <label className="rc-label">開催日 ／ 曜日表記</label>
                      <div className="rc-date-label">
                        <input
                          type="date"
                          value={form.eventDate}
                          onChange={(e) => set("eventDate", e.target.value)}
                          onFocus={() => focusPreview(".rct-ev .rows")}
                        />
                        <input
                          type="text"
                          maxLength={20}
                          placeholder="（祝月）"
                          value={form.eventWeekdayLabel}
                          onChange={(e) => set("eventWeekdayLabel", e.target.value)}
                          onFocus={() => focusPreview(".rct-ev .rows")}
                        />
                      </div>
                    </div>
                    <div className="rc-field">
                      <label className="rc-label">会場名</label>
                      <input
                        type="text"
                        value={form.venue}
                        onChange={(e) => set("venue", e.target.value)}
                        onFocus={() => focusPreview(".rct-ev .rows")}
                      />
                    </div>
                    <div className="rc-field">
                      <label className="rc-label">住所</label>
                      <input
                        type="text"
                        value={form.address}
                        onChange={(e) => set("address", e.target.value)}
                        onFocus={() => focusPreview(".rct-ev .rows")}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="rc-section">参加規約</div>
              <div className="rc-group">
                <div className="rc-field">
                  <textarea
                    value={form.terms}
                    onChange={(e) => set("terms", e.target.value)}
                    onFocus={() => focusPreview(".rct-terms")}
                  />
                </div>
              </div>

              <div className="rc-section">料金</div>
              <div className="rc-group">
                  <div className="rc-field">
                    <label className="rc-label">一般</label>
                    <div className="rc-pair">
                      <input
                        type="text"
                        maxLength={20}
                        placeholder="区分名"
                        value={form.labelGeneral}
                        onChange={(e) => set("labelGeneral", e.target.value)}
                        onFocus={() => focusPreview(".rct-fee")}
                      />
                      <input
                        type="number"
                        min="0"
                        placeholder="例：30000"
                        value={form.feeGeneral}
                        onChange={(e) => set("feeGeneral", e.target.value)}
                        onFocus={() => focusPreview(".rct-fee")}
                      />
                    </div>
                  </div>
                  <div className="rc-field">
                    <label className="rc-label">兄弟</label>
                    <div className="rc-pair">
                      <input
                        type="text"
                        maxLength={20}
                        placeholder="区分名"
                        value={form.labelSibling}
                        onChange={(e) => set("labelSibling", e.target.value)}
                        onFocus={() => focusPreview(".rct-fee")}
                      />
                      <input
                        type="number"
                        min="0"
                        placeholder="例：25000"
                        value={form.feeSibling}
                        onChange={(e) => set("feeSibling", e.target.value)}
                        onFocus={() => focusPreview(".rct-fee")}
                      />
                    </div>
                  </div>
                  <div className="rc-field">
                    <label className="rc-label">幼少</label>
                    <div className="rc-pair">
                      <input
                        type="text"
                        maxLength={20}
                        placeholder="区分名"
                        value={form.labelYoung}
                        onChange={(e) => set("labelYoung", e.target.value)}
                        onFocus={() => focusPreview(".rct-fee")}
                      />
                      <input
                        type="number"
                        min="0"
                        placeholder="例：10000"
                        value={form.feeYoung}
                        onChange={(e) => set("feeYoung", e.target.value)}
                        onFocus={() => focusPreview(".rct-fee")}
                      />
                    </div>
                  </div>
              </div>

              <div className="rc-section">締切日時</div>
              <div className="rc-group">
                <div className="rc-field">
                  <label className="rc-label">参加申込</label>
                  <input
                    type="datetime-local"
                    value={form.entryDeadline}
                    onChange={(e) => set("entryDeadline", e.target.value)}
                  />
                </div>
                <div className="rc-field">
                  <label className="rc-label">パンフレット写真提出</label>
                  <input
                    type="datetime-local"
                    value={form.photoDeadline}
                    onChange={(e) => set("photoDeadline", e.target.value)}
                  />
                </div>
                <div className="rc-field">
                  <label className="rc-label">音源・照明提出</label>
                  <input
                    type="datetime-local"
                    value={form.submissionDeadline}
                    onChange={(e) => set("submissionDeadline", e.target.value)}
                  />
                </div>
              </div>

              <div className="rc-section">パンフレット写真</div>
              <div className="rc-group">
                <div className="rc-field">
                  <label className="rc-label">
                    注意事項（1行＝1項目・申込ページに赤字で表示）
                  </label>
                  <textarea
                    placeholder={"例：\n背景は無地（白い壁など）で撮影してください\nお顔がはっきり写ったものをご用意ください\n加工・文字入れ・スタンプは不可です"}
                    value={form.photoNotes}
                    onChange={(e) => set("photoNotes", e.target.value)}
                  />
                </div>
              </div>

              <div className="rc-section">お支払い（振込）</div>
              <div className="rc-group">
                <div className="rc-field">
                  <label className="rc-label">振込期限</label>
                  <input
                    type="date"
                    value={form.transferDeadline}
                    onChange={(e) => set("transferDeadline", e.target.value)}
                  />
                </div>
                <div className="rc-field">
                  <label className="rc-label">振込先口座情報</label>
                  <textarea
                    placeholder={"例：\n〇〇銀行 〇〇支店 普通 1234567\n口座名義 アイヴィダンススタジオ"}
                    value={form.transferInfo}
                    onChange={(e) => set("transferInfo", e.target.value)}
                  />
                </div>
                <div className="rc-field">
                  <label className="rc-label">振込に関する注意書き</label>
                  <textarea
                    placeholder={"例：\n※お振込名義は「会員番号＋お名前」でお願いいたします（入金確認のため）。"}
                    value={form.transferNote}
                    onChange={(e) => set("transferNote", e.target.value)}
                  />
                </div>
              </div>

              <div className="rc-section">点呼表・タイムテーブル</div>
              <div className="rc-group">
                <div className="rc-row">
                  <div className="rc-field">
                    <label className="rc-label">部数</label>
                    <input
                      type="number"
                      min="1"
                      value={form.numParts}
                      onChange={(e) => set("numParts", e.target.value)}
                    />
                  </div>
                  <div className="rc-field">
                    <label className="rc-label">転換時間（秒）</label>
                    <input
                      type="number"
                      min="0"
                      value={form.defaultTransitionSeconds}
                      onChange={(e) =>
                        set("defaultTransitionSeconds", e.target.value)
                      }
                    />
                  </div>
                </div>
                <p className="rc-hint">
                  部数を設定すると、演目順ページで各部に演目を振り分けられます。
                </p>
                <div className="rc-row">
                  {Array.from({
                    length: Math.max(1, Number(form.numParts) || 1),
                  }).map((_, i) => (
                    <div className="rc-field" key={i}>
                      <label className="rc-label">
                        {(Number(form.numParts) || 1) > 1
                          ? `開演時刻（${i + 1}部）`
                          : "開演時刻"}
                      </label>
                      <input
                        type="time"
                        value={(form.partOpenTimes && form.partOpenTimes[i]) || ""}
                        onChange={(e) => setPartOpen(i, e.target.value)}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="rc-section">インストラクター</div>
              <div className="rc-group">
                <button
                  type="button"
                  className="rc-btn"
                  onClick={openTokenModal}
                >
                  ログインURL一覧を表示
                </button>
              </div>
            </div>

            <div className="submit-area">
              <button type="button" onClick={handleReset}>
                Reset
              </button>
              <button type="button" onClick={handleSubmit}>
                Submit
              </button>
            </div>
          </>
        )}
      </ResizableEditArea>

      <iframe
        ref={iframeRef}
        title="申込ページ プレビュー"
        src={`${ORIGIN}/recital/2026/entry?preview=1`}
        onLoad={post}
      />

      {tokenModal && (
        <div className="rc-modal-ov" onClick={() => setTokenModal(false)}>
          <div
            className="rc-modal rc-token-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mh">
              インストラクター ログインURL
              <span className="x" onClick={() => setTokenModal(false)}>
                ✕
              </span>
            </div>
            <div className="mb">
              {instructors === null ? (
                <p className="rc-tk-empty">読み込んでいます…</p>
              ) : instructors.length === 0 ? (
                <p className="rc-tk-empty">
                  演目に紐づくインストラクターがいません。
                </p>
              ) : (
                instructors.map((row) => (
                  <div className="rc-tk-row" key={row.instructor_id}>
                    <div className="rc-tk-name">{row.name}</div>
                    <div className="rc-tk-url">
                      <input
                        type="text"
                        readOnly
                        value={instructorUrl(row.token)}
                      />
                      <button type="button" onClick={() => copyUrl(row)}>
                        {copiedId === row.instructor_id ? "コピー済" : "コピー"}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
